import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  views: '#3b82f6',      // Blue
  generated: '#8b5cf6',  // Purple
  sent: '#10b981',       // Green
  leads: '#f59e0b'       // Amber
};

const METRIC_LABELS = {
  views: 'Views',
  generated: 'Emails Generated',
  sent: 'Emails Sent',
  leads: 'Leads'
};

const METRIC_DESCRIPTIONS = {
  views: 'Total number of times your campaign widget was viewed by visitors',
  generated: 'Number of times users prompted the AI to generate an email',
  sent: 'Number of times users clicked the Send button to send their email',
  leads: 'New contacts collected through your campaign forms'
};

const TIMEZONE_OPTIONS = [
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne' },
  { value: 'Australia/Brisbane', label: 'Australia/Brisbane' },
  { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Australia/Adelaide' },
  { value: 'Australia/Darwin', label: 'Australia/Darwin' },
  { value: 'Australia/Hobart', label: 'Australia/Hobart' },
  { value: 'Pacific/Auckland', label: 'New Zealand' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'Europe/London', label: 'UK (GMT/BST)' }
];

export default function AnalyticsChart({ token, campaignId = null }) {
  const [interval, setInterval] = useState('day');
  const [activeMetrics, setActiveMetrics] = useState(['generated', 'leads']);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7'); // days: 1, 2, 7, 30, 90, or 'custom'
  const [timezone, setTimezone] = useState('Australia/Sydney');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Load saved timezone preference
  useEffect(() => {
    const savedTz = localStorage.getItem('analytics_timezone');
    if (savedTz) setTimezone(savedTz);
  }, []);

  // Save timezone preference
  const handleTimezoneChange = (tz) => {
    setTimezone(tz);
    localStorage.setItem('analytics_timezone', tz);
  };

  useEffect(() => {
    fetchData();
  }, [interval, campaignId, token, dateRange, timezone, customStartDate, customEndDate]);

  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({ interval, metric: 'all', tz: timezone });
      
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      } else {
        params.append('days', dateRange);
      }
      
      if (campaignId) params.append('campaign_id', campaignId);
      
      const res = await fetch(`${API}/api/analytics/timeseries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch analytics');
      
      const data = await res.json();
      
      // Use periods from backend (respects timezone) to fill gaps with zeros
      const merged = mergeTimeseriesData(data.data, data.periods || []);
      setChartData(merged);
    } catch (err) {
      setError(err.message);
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const mergeTimeseriesData = (data, periods) => {
    // Use periods from backend if available, otherwise fall back to local generation
    const allDates = periods.length > 0 ? periods : generateAllPeriods();
    
    // Build merged dataset with 0 for missing periods
    return allDates.map(date => {
      const point = { date: formatDate(date, interval) };
      
      Object.keys(COLORS).forEach(metric => {
        const metricData = data[metric] || [];
        const found = metricData.find(item => item.date === date);
        point[metric] = found ? found.count : 0;
      });
      
      return point;
    });
  };

  // Generate all time periods for the selected range
  const generateAllPeriods = () => {
    const periods = [];
    const now = new Date();
    let daysBack = parseInt(dateRange) || 7;
    
    if (interval === 'hour') {
      // Hourly: generate all hours in range (max 48)
      const hoursBack = Math.min(daysBack * 24, 48);
      for (let i = hoursBack - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setHours(d.getHours() - i, 0, 0, 0);
        // Format as ISO hour string
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hour = String(d.getHours()).padStart(2, '0');
        periods.push(`${year}-${month}-${day}T${hour}:00:00`);
      }
    } else if (interval === 'day') {
      // Daily: generate all days in range
      for (let i = daysBack - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        periods.push(`${year}-${month}-${day}`);
      }
    } else if (interval === 'week') {
      // Weekly: generate all weeks in range
      const weeksBack = Math.ceil(daysBack / 7);
      for (let i = weeksBack - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - (i * 7));
        // Get ISO week number
        const startOfYear = new Date(d.getFullYear(), 0, 1);
        const weekNum = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
        periods.push(`${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`);
      }
    }
    
    return periods;
  };

  const formatDate = (dateStr, interval) => {
    if (interval === 'hour') {
      // Format: "2025-01-20T14:00:00" -> "Jan 20 2pm"
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }) + 
             ' ' + d.toLocaleTimeString('en-AU', { hour: 'numeric' });
    } else if (interval === 'week') {
      // Format: "2025-W03" -> "Week 3"
      const weekNum = dateStr.split('-W')[1];
      return `Week ${parseInt(weekNum)}`;
    } else {
      // Format: "2025-01-20" -> "Jan 20"
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
    }
  };

  const toggleMetric = (metric) => {
    setActiveMetrics(prev => {
      if (prev.includes(metric)) {
        // Don't allow removing the last metric
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== metric);
      } else {
        return [...prev, metric];
      }
    });
  };

  const intervalButtons = [
    { value: 'hour', label: 'Hourly' },
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' }
  ];

  const metricButtons = [
    { value: 'generated', label: 'Generated' },
    { value: 'leads', label: 'Leads' }
  ];

  const dateRangeOptions = [
    { value: '1', label: 'Last 24 hours' },
    { value: '2', label: 'Last 48 hours' },
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: 'custom', label: 'Custom range' }
  ];

  // When switching to hourly, limit to 48 hours max
  const handleIntervalChange = (newInterval) => {
    setInterval(newInterval);
    if (newInterval === 'hour') {
      // Limit hourly view to 48 hours
      if (!['1', '2'].includes(dateRange)) {
        setDateRange('2');
      }
    }
  };

  // Filter date range options based on interval
  const getDateRangeOptions = () => {
    if (interval === 'hour') {
      // Hourly view: max 48 hours
      return [
        { value: '1', label: 'Last 24 hours' },
        { value: '2', label: 'Last 48 hours' },
        { value: 'custom', label: 'Custom range (max 48h)' }
      ];
    }
    return dateRangeOptions;
  };

  // Info icon component with tooltip
  const InfoTooltip = ({ text }) => (
    <span className="relative group ml-1 cursor-help">
      <svg className="w-4 h-4 text-gray-400 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" strokeWidth="2"/>
        <path strokeWidth="2" d="M12 16v-4m0-4h.01"/>
      </svg>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
        {text}
      </span>
    </span>
  );

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-red-500">Error loading analytics: {error}</p>
          <Button onClick={fetchData} className="mt-2">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="analytics-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium">Analytics Over Time</CardTitle>
        
        {/* Controls */}
        <div className="flex flex-wrap gap-4 mt-3">
          {/* Timezone selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Timezone:</span>
            <select 
              value={timezone} 
              onChange={(e) => handleTimezoneChange(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              data-testid="timezone-select"
            >
              {TIMEZONE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          {/* Date range selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Period:</span>
            <select 
              value={dateRange} 
              onChange={(e) => {
                setDateRange(e.target.value);
                setShowCustomRange(e.target.value === 'custom');
              }}
              className="border rounded px-2 py-1 text-sm"
              data-testid="date-range-select"
            >
              {getDateRangeOptions().map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          {/* Interval selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">View by:</span>
            <div className="flex gap-1">
              {intervalButtons.map(btn => (
                <Button
                  key={btn.value}
                  variant={interval === btn.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleIntervalChange(btn.value)}
                  data-testid={`interval-${btn.value}`}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Metric toggles with info tooltips */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Show:</span>
            <div className="flex gap-1">
              {metricButtons.map(btn => (
                <Button
                  key={btn.value}
                  variant={activeMetrics.includes(btn.value) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleMetric(btn.value)}
                  data-testid={`metric-${btn.value}`}
                  className="relative group"
                  style={{
                    backgroundColor: activeMetrics.includes(btn.value) ? COLORS[btn.value] : undefined,
                    borderColor: COLORS[btn.value],
                    color: activeMetrics.includes(btn.value) ? 'white' : COLORS[btn.value]
                  }}
                  title={METRIC_DESCRIPTIONS[btn.value]}
                >
                  {btn.label}
                  <InfoTooltip text={METRIC_DESCRIPTIONS[btn.value]} />
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Custom date range inputs */}
        {showCustomRange && (
          <div className="flex items-center gap-4 mt-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
                data-testid="custom-start-date"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
                data-testid="custom-end-date"
              />
            </div>
            <Button 
              size="sm" 
              onClick={fetchData}
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-gray-500">
            No data available for the selected period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Legend />
              
              {activeMetrics.includes('views') && (
                <Line
                  type="monotone"
                  dataKey="views"
                  name={METRIC_LABELS.views}
                  stroke={COLORS.views}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
              
              {activeMetrics.includes('generated') && (
                <Line
                  type="monotone"
                  dataKey="generated"
                  name={METRIC_LABELS.generated}
                  stroke={COLORS.generated}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
              
              {activeMetrics.includes('sent') && (
                <Line
                  type="monotone"
                  dataKey="sent"
                  name={METRIC_LABELS.sent}
                  stroke={COLORS.sent}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
              
              {activeMetrics.includes('leads') && (
                <Line
                  type="monotone"
                  dataKey="leads"
                  name={METRIC_LABELS.leads}
                  stroke={COLORS.leads}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
